import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { X } from 'lucide-react';
import { vendorInvoicesAPI } from '../services/api';
import { errorModal } from './ErrorModal';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface VendorProgressChartProps {
  isOpen: boolean;
  onClose: () => void;
  vendorId: number;
  vendorName: string;
}

const VendorProgressChart: React.FC<VendorProgressChartProps> = ({
  isOpen,
  onClose,
  vendorId,
  vendorName
}) => {
  const [chartData, setChartData] = useState<any>(null);
  const [chartLoading, setChartLoading] = useState(false);

  const fetchVendorChartData = async (vendorId: number) => {
    setChartLoading(true);
    try {
      const response = await vendorInvoicesAPI.getVendorMonthlyStats(vendorId);
      if (response.success) {
        const { monthlyStats } = response.data;
        
        // Prepare chart data
        const chartData = {
          labels: monthlyStats.map((stat: any) => stat.month),
          datasets: [
            {
              label: 'Amount',
              data: monthlyStats.map((stat: any) => stat.totalAmount),
              backgroundColor: 'rgba(59, 130, 246, 0.5)',
              borderColor: 'rgba(59, 130, 246, 1)',
              borderWidth: 1,
            },
          ],
        };
        
        setChartData(chartData);
      } else {
        errorModal('Failed to fetch vendor statistics');
      }
    } catch (error) {
      console.error('Error fetching vendor chart data:', error);
      errorModal('Error fetching vendor statistics');
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && vendorId) {
      fetchVendorChartData(vendorId);
    }
  }, [isOpen, vendorId]);

  const handleClose = () => {
    setChartData(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {vendorName} - Last 12 Months Progress
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="h-96">
          {chartLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : chartData ? (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text: 'Monthly Invoice Amounts',
                    font: {
                      size: 16,
                    },
                  },
                  legend: {
                    display: true,
                    position: 'top',
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Amount',
                    },
                  },
                  x: {
                    title: {
                      display: true,
                      text: 'Month',
                    },
                  },
                },
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              No data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorProgressChart;
